import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, type HetznerServerOptions, type Server } from '../api';
import { AgentInstallPanel, type AgentInstallState, type ReadinessSummaryCounts } from '../components/AgentInstallPanel';
import { Field, Input, Segmented } from '../components/primitives';
import { HetznerServerFields, emptyHetznerForm, selectedServerType } from '../components/server/HetznerServerFields';
import type { HetznerServerForm } from '../components/server/HetznerServerFields';
import { OnboardingLayout } from '../components/OnboardingLayout';
import { ErrorNotice } from '../components/ui';

/** Name the server, install the agent with one command, then the agent
 * connects and checks the server by itself. */
const stepsFor = (mode: Mode) => [
  { key: 'name', label: mode === 'hetzner' ? 'Choose the server' : 'Name the server' },
  { key: 'install', label: mode === 'hetzner' ? 'Start the server' : 'Install the agent' },
  { key: 'check', label: 'Server check' },
];

type Mode = 'existing' | 'hetzner';

/** A new server's entry, and the install command when the person runs it. */
interface Created {
  server: Server;
  installationCommand?: string;
}

/**
 * ServerOnboardingPage connects one Linux server to Commerce Ops: name it,
 * run the generated install command on it, watch the agent connect and the
 * server check complete. Nothing here asks for SSH credentials — the agent
 * is the connection. It uses the shared onboarding layout (CO-90); its
 * steps follow the agent, so a finished step cannot be revisited — the
 * server exists once its install command is generated.
 */
export function ServerOnboardingPage({ isOwner = false }: { isOwner?: boolean }) {
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>('existing');
  const steps = stepsFor(mode);
  const [hetzner, setHetzner] = useState<HetznerServerForm>(emptyHetznerForm);
  const [hetznerOptions, setHetznerOptions] = useState<HetznerServerOptions | null>(null);
  const [serverName, setServerName] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [invalidAttempt, setInvalidAttempt] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [createResult, setCreateResult] = useState<Created | null>(null);
  const [installState, setInstallState] = useState<AgentInstallState>('waiting');
  const [checkSummary, setCheckSummary] = useState<ReadinessSummaryCounts | null>(null);

  const handleCreate = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (!serverName.trim()) {
      setNameError('Name the server.');
      setInvalidAttempt((n) => n + 1);
      return;
    }
    if (mode === 'hetzner' && (!hetzner.tokenId || !hetzner.location || !hetzner.serverType)) {
      setError(new Error('Save a token and choose a size.'));
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      if (mode === 'hetzner') {
        const created = await api.createHetznerServer({
          name: serverName.trim(),
          token_id: hetzner.tokenId,
          location: hetzner.location,
          server_type: hetzner.serverType,
          ssh_public_key: hetzner.sshKey.trim() || undefined,
        });
        setCreateResult({ server: created.server });
      } else {
        const created = await api.createServer(serverName.trim());
        setCreateResult({ server: created.server, installationCommand: created.installation_command });
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsSubmitting(false);
    }
  };

  const breadcrumbs = [{ label: 'Servers', to: '/app/servers' }, { label: 'Connect a server' }];

  const chosenType = selectedServerType(hetznerOptions, hetzner);
  const hetznerPrice = chosenType && chosenType.monthly_eur > 0 ? `€${chosenType.monthly_eur.toFixed(2)} / month` : '';

  if (!createResult) {
    return (
      <OnboardingLayout
        breadcrumbs={breadcrumbs}
        steps={steps}
        current={0}
        stepsLabel="Connect a server steps"
        title="Connect a server"
        intro="The Commerce Ops Agent connects outbound over HTTPS, so no inbound port is opened and no SSH credentials are stored."
        onSubmit={handleCreate}
        invalidAttempt={invalidAttempt}
        cancel={{ to: '/app/servers' }}
        primary={
          <button type="submit" className="button button--primary" disabled={isSubmitting || (mode === 'hetzner' && (!isOwner || !hetzner.tokenId || !hetzner.serverType))} data-testid="server-submit">
            {mode === 'hetzner'
              ? isSubmitting
                ? 'Creating'
                : hetznerPrice
                  ? `Create server · ${hetznerPrice} net`
                  : 'Create server'
              : isSubmitting
                ? 'Generating'
                : 'Generate install command'}
          </button>
        }
      >
        <ErrorNotice error={error} />
        <Segmented
          label="Where the server is"
          options={[
            { value: 'existing', label: 'I have a server', testId: 'server-mode-existing' },
            { value: 'hetzner', label: 'Create one on Hetzner', testId: 'server-mode-hetzner' },
          ]}
          value={mode}
          onChange={(next) => {
            setMode(next);
            setError(null);
          }}
        />
        <Field
          id="server-name"
          label="Server name"
          error={nameError}
          className="onboarding-field--short"
        >
          <Input
            type="text"
            placeholder="e.g. prod-web-01"
            value={serverName}
            onChange={(e) => {
              setServerName(e.target.value);
              setNameError(null);
            }}
            autoComplete="off"
            spellCheck={false}
            autoFocus
          />
        </Field>
        {mode === 'hetzner' ? <HetznerServerFields isOwner={isOwner} form={hetzner} onChange={setHetzner} onOptions={setHetznerOptions} /> : null}
        {mode === 'hetzner' && hetznerPrice ? <p className="muted" data-testid="hetzner-price">Billed by Hetzner to your account</p> : null}
      </OnboardingLayout>
    );
  }

  // The check finishing is not the check passing: a failed check is a
  // finished flow that still needs a person (CO-184).
  const finished = installState === 'complete';
  const problems = finished && checkSummary ? checkSummary.failed : 0;
  const complete = finished && checkSummary !== null && problems === 0;
  const current = installState === 'waiting' || installState === 'expired' ? 1 : complete ? steps.length : 2;
  const title = complete
    ? `${createResult.server.name} is connected`
    : problems > 0
      ? `The server check found ${problems} ${problems === 1 ? 'problem' : 'problems'}`
      : installState === 'expired'
        ? 'The install command has expired'
        : installState === 'check_stalled'
          ? 'The server check has not finished'
          : finished
            ? 'The server check has finished'
            : installState === 'waiting'
              ? mode === 'hetzner'
                ? 'Starting the server'
                : 'Install the agent'
              : 'Checking the server';
  return (
    <OnboardingLayout
      breadcrumbs={breadcrumbs}
      steps={steps}
      current={current}
      stepsLabel="Connect a server steps"
      title={title}
      secondary={
        <Link to="/app/projects/new" className="button button--secondary">
          Create a project on this server
        </Link>
      }
      primary={
        <button
          type="button"
          className={`button ${finished ? 'button--primary' : 'button--secondary'}`}
          onClick={() => navigate(`/app/servers/${createResult.server.id}`)}
        >
          {finished ? 'Open server' : 'Open server page'}
        </button>
      }
    >
      <AgentInstallPanel
        serverId={createResult.server.id}
        installationCommand={createResult.installationCommand}
        onStateChange={(state, details) => {
          setInstallState(state);
          setCheckSummary(details.summary);
        }}
      />
    </OnboardingLayout>
  );
}
